// API client for Buddy (Claude Skill) Overview configuration.
//
// Talks to the claude-skills-msvc "Claude Buddies API" (FastAPI). The base URL
// is taken from `VITE_BASEURL` via the shared axios instance. A "buddy" in that
// service is a skill, and its editable *config* is the Overview-page config:
//   { icon, popular_tasks[], sample_prompts[], pairs_with[] }
// which exists independently for three owner scopes: master, brand, user.

import apiClient from '@lift-admin/utils/apiClient';
import { Toastify } from 'lift-ui';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';

export type OverviewScope = 'master' | 'brand' | 'user';

/** Owner identifiers required to address a single overview config. */
export interface OverviewOwnerRef {
  scope: OverviewScope;
  /** master skill id (master), OR the fork's copy_id (brand/user). */
  ownerId: string;
  /** Required for brand scope. */
  brandId?: number | string;
  /** Required for user scope. */
  userId?: number | string;
}

/** One row in the master skills list (subset of the raw skill row we use). */
export interface MasterSkillListItem {
  id: string;
  slug: string;
  display_title: string;
  description?: string | null;
  latest_version?: string;
  source?: string;
}

/** One row in `GET /api/brands/{brand_id}/skills` or the user equivalent. */
export interface SkillCopySummary {
  id: string; // copy_id
  brand_id?: number;
  user_id?: number;
  master_skill_id: string;
  slug: string;
  display_title: string;
  description?: string | null;
  sync_status?: string;
}

/** Response of every `.../overview` GET/PUT (SkillOverviewConfig). */
export interface SkillOverviewConfig {
  owner_type: OverviewScope;
  owner_id: string;
  icon: string | null;
  popular_tasks: string[];
  sample_prompts: string[];
  pairs_with: string[];
  configured: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Body of the overview PUT (UpsertSkillOverviewConfigRequest). */
export interface UpsertOverviewPayload {
  icon?: string | null;
  popular_tasks: string[];
  sample_prompts: string[];
  pairs_with: string[];
}

export const OVERVIEW_MAX_ITEMS = 25;
export const OVERVIEW_MAX_ITEM_LEN = 500;
export const OVERVIEW_MAX_ICON_LEN = 255;

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  next_offset: number | null;
  has_more: boolean;
}

function errorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

/** Build the `.../overview` path for a given owner reference. */
function overviewPath(ref: OverviewOwnerRef): string {
  switch (ref.scope) {
    case 'master':
      return `/claude-skill/api/skills/${encodeURIComponent(ref.ownerId)}/overview`;
    case 'brand':
      return `/claude-skill/api/brands/${ref.brandId}/skills/${encodeURIComponent(ref.ownerId)}/overview`;
    case 'user':
      return `/claude-skill/api/users/${ref.userId}/skills/${encodeURIComponent(ref.ownerId)}/overview`;
    default:
      throw new Error(`Unknown overview scope: ${(ref as OverviewOwnerRef).scope}`);
  }
}

// -------------------- Owner pickers (brands / users) ----------------------

export interface BrandOption {
  id: number;
  name: string;
}

export interface UserOption {
  id: number;
  name: string;
  email: string;
}

/** GET /brand/v1/admin/brands/all — full brand list for the picker. */
export async function listBrands(): Promise<BrandOption[]> {
  const { data } = await apiClient.get('/brand/v1/admin/brands/all');
  return (data?.response ?? []) as BrandOption[];
}

/** GET /user/v1/admin/all — full user list for the picker. */
export async function listUsers(): Promise<UserOption[]> {
  const { data } = await apiClient.get('/user/v1/admin/all');
  return (data?.response ?? []) as UserOption[];
}

// -------------------- List endpoints (to pick a buddy) --------------------

/** GET /api/skills — paginated list of master skills. */
export async function listMasterSkills(
  limit = 200,
  offset = 0
): Promise<{ data: MasterSkillListItem[]; pagination: PaginationInfo }> {
  const { data } = await apiClient.get('/claude-skill/api/skills', { params: { limit, offset } });
  return data;
}

/** GET /api/brands/{brand_id}/skills — buddy forks assigned to a brand. */
export async function listBrandSkills(brandId: number | string): Promise<SkillCopySummary[]> {
  const { data } = await apiClient.get(`/claude-skill/api/brands/${brandId}/skills`);
  return data;
}

/** GET /api/users/{user_id}/skills — buddy forks assigned to a user. */
export async function listUserSkills(userId: number | string): Promise<SkillCopySummary[]> {
  const { data } = await apiClient.get(`/claude-skill/api/users/${userId}/skills`);
  return data;
}

// -------------------- Overview config (the "config part") -----------------

/** GET the overview config for one buddy owner. Always returns a stable shape. */
export async function getOverviewConfig(ref: OverviewOwnerRef): Promise<SkillOverviewConfig> {
  const { data } = await apiClient.get<SkillOverviewConfig>(overviewPath(ref));
  return data;
}

/**
 * PUT (full replace) the overview config for one buddy owner.
 * Sends the complete lists each time — the backend replaces what it stores.
 */
export async function upsertOverviewConfig(
  ref: OverviewOwnerRef,
  payload: UpsertOverviewPayload,
  buddyName?: string
): Promise<SkillOverviewConfig> {
  const url = overviewPath(ref);
  const label = buddyName || ref.ownerId;
  try {
    const { data } = await apiClient.put<SkillOverviewConfig>(url, payload);
    Toastify('success', 'Buddy config saved');

    await logAdminAction(`Buddy config for "${label}" (${ref.scope}) updated`, {
      url,
      method: 'PUT',
      status: 200,
      category: 'settings',
      action: 'update_buddy_config',
      buddyName: label,
      details: [
        { label: 'Scope', value: ref.scope },
        { label: 'Icon', value: payload.icon || '—' },
        { label: 'Popular Tasks', value: String(payload.popular_tasks.length) },
        { label: 'Sample Prompts', value: String(payload.sample_prompts.length) },
        { label: 'Pairs With', value: String(payload.pairs_with.length) },
      ],
    });

    return data;
  } catch (error: any) {
    const message = errorMessage(error, 'Failed to save buddy config');
    Toastify('error', message);
    await logAdminAction(
      `Failed to update buddy config for "${label}" (${ref.scope}): ${message}`,
      {
        url,
        method: 'PUT',
        category: 'settings',
        action: 'update_buddy_config',
        buddyName: label,
      },
      true
    );
    throw error;
  }
}
